import { Driver, Geotab, Group, GroupFilterCondition } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_USER } from "@/constants/resultsLimits";
import createUserGroupFilter from "@/utilities/createUserGroupFilter";

/**
 * Loads the active drivers in the selected groups and keeps those with a driver key.
 *
 * At most {@link RESULTS_LIMIT_USER} users come back (Geotab rejects a larger User request), so
 * `isTruncated` is set when the cap is reached and drivers may be missing.
 */
export async function fetchDriversWithKeys(
  geotabClient: Geotab,
  groups?: Group[],
  groupFilterCondition?: GroupFilterCondition,
  signal?: AbortSignal,
): Promise<{ drivers: Driver[]; isTruncated: boolean }> {
  const now = new Date();
  const search = createUserGroupFilter(groups, groupFilterCondition, {
    // UserSearch.isDriver: drivers only, so non-drivers do not eat the cap. Not combined with
    // `userSearchType`: Geotab rejects "You can only specify IsDriver or UserSearchType".
    isDriver: true,
    fromDate: now,
    toDate: now,
  });
  const users = await geotabClient.get("User", search, RESULTS_LIMIT_USER, signal);
  const drivers = users.filter(u => u.isDriver && ((u as Driver).keys?.length ?? 0) > 0) as Driver[];
  return { drivers, isTruncated: users.length >= RESULTS_LIMIT_USER };
}

/** The active drivers with a driver key in the selected groups, see {@link fetchDriversWithKeys}. */
export function useUsersWithKeys() {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { groups, groupFilterCondition } = useAddInState();
  return useQuery({
    queryKey: ["usersWithKeys", activeSessionId, groups, groupFilterCondition],
    queryFn: ({ signal }) => fetchDriversWithKeys(geotabClient, groups, groupFilterCondition, signal),
  });
}
